<template>
  <div v-if="settings" class="h-full w-full flex flex-col justify-between overflow-y-auto text-app-color">
    <div class="w-full max-w-300 mx-auto md:max-w-full md:pl-100 md:pr-50 pt-35 md:pt-67 pb-50">
      <div v-if="showBackButton" class="flex items-center md:hidden" @click="back">
        <i class="text-12 mr-10 icon-keyboard-arrow-left opacity-50" />
        <p class="text-opacity-80">Back</p>
      </div>

      <h2 class=" mb-30 font-bold text-35 md:text-21">Design</h2>

      <!-- T Y P O G R A P H Y  P R E V I E W -->
      <div class="px-15 py-10 md:px-35 md:py-25 rounded-8" :class="isDarkMode ? 'bg-hover' : 'bg-grey-450'">
        <p class="font-semibold">Typography preview</p>
        <p class="pt-5 text-1rem" :class="`font-${settings.fontFamily.split(' ').join('')}`">Then came the night, the side of the gibbous moon was in shadow.</p>
        <p class="pt-5 text-1rem" :class="`font-${settings.fontFamily.split(' ').join('')}`">The quick brown fox jumps over the lazy dog.</p>
      </div>

      <!-- Font Family -->
      <div id="font-family" class="py-30 w-full">
        <p class="text-17 font-bold">Font family</p>
        <div class="flex flex-wrap -mx-10 md:pr-50">
          <ButtonModule
            v-for="family in fontFamilies"
            :key="family"
            :text="family"
            class="mt-20 mx-5 border-2 rounded-8 h-40 px-15 hover:border-main-color hover:text-main-color transition-all duration-300"
            :class="{ 'border-main-color text-main-color border-3': settings.fontFamily === family }"
            :icon-has-margin="false"
            :prefix="settings.fontFamily === family ? 'icon-check mr-3 text-21' : ''"
            @click="update('fontFamily', family)"
          />
        </div>
      </div>

      <!-- Font Sizes -->
      <div id="font-size" class="pt-30 pb-30">
        <p class="text-17 font-bold">Font size</p>
        <div class="flex flex-wrap -mx-10">
          <ButtonModule
            v-for="(size, index) in fontSizes"
            :key="size"
            :text="size"
            class="mt-20 mx-5 border-2 rounded-8 h-40 px-15 hover:border-main-color hover:text-main-color transition-all duration-300"
            :class="[`text-${14 + index}`, { 'border-main-color text-main-color border-3': settings.fontSize === size }]"
            :icon-has-margin="false"
            :prefix="settings.fontSize === size ? 'icon-check mr-3 text-21' : ''"
            @click="update('fontSize', size)"
          />
        </div>
      </div>

      <div class="pt-30 pb-30 w-full border-b border-hover">
        <p class="text-17 font-bold">Line height</p>
        <div class="max-w-250 pt-30 pb-30">
          <VueSlider
            v-model="lineHeight"
            class="cursor-pointer"
            :marks="marks"
            :min="1.5"
            :max="1.9"
            :interval="0.1"
            :process-style="{ backgroundColor: 'transparent' }"
            :rail-style="{ backgroundColor: '#ddd' }"
            :label-style="{ color: isDarkMode ? '#ddd' : '#364962' }"
            :tooltip="'none'"
            @change="v => update('lineHeight', String(v))"
          >
            <template #dot="{ focus }">
              <div class="w-full h-full rounded-full bg-main-color" :class="focus" />
            </template>

            <template #step>
              <div class="h-20 w-2 bg-grey-700 absolute -top-8 ml-1" />
            </template>
          </VueSlider>
        </div>
      </div>

      <!-- Themes -->
      <Themes :themes="themes" />

      <!-- Mode -->
      <div id="document-mode">
        <p class="font-bold text-17 pt-30">Canvas mode</p>
        <div class="flex mt-20 pb-30 border-b border-hover">
          <RadioModule v-for="mode in modes" :key="mode" class="mr-20" :value="settings.mode" :has-border="true"
                       :label="mode" :input-value="mode" group="test"
                       :label-class="['text-14 mt-5 ml-15', {'font-bold': settings.mode === mode}]"
                       @input="v => update('mode', v)"
          >
            <div class="cursor-pointer">
              <div class="h-105 w-140 rounded-8">
                <img class="w-full" :src="require(`@/assets/images/settings/${isDarkMode ? `${mode} Dark`: mode}.svg`)" alt="mode">
              </div>
            </div>
          </RadioModule>
        </div>
      </div>

      <!-- Column width -->
      <p class="hidden md:block font-bold mt-30 text-17">Column width</p>
      <div class="hidden md:flex flex-wrap -mx-10 pb-30 border-b border-hover">
        <RadioModule v-for="(column) in columns" :key="column" :value="settings.columnWidth" :has-border="true"
                     class="mt-20 mx-10"
                     :input-value="column" :label="column" group="test"
                     :label-class="['text-14 mt-5 ml-15', {'font-bold': settings.columnWidth === column}, { 'ml-30': column === 'Wide'}]"
                     @input="v => update('columnWidth', v)"
        >
          <div class="cursor-pointer">
            <div class="h-105 w-140">
              <img class="w-full" :src="require(`@/assets/images/settings/${isDarkMode ? `${column} Dark`: column}.svg`)" alt="mode">
            </div>
          </div>
        </RadioModule>
      </div>

      <!-- Default icon for new projects -->
      <div class="mt-30 pb-30 w-full">
        <p class="text-17 font-bold">Default icon for new pages</p>
        <div
          class="flex items-center justify-center h-105 w-140 rounded-6 cursor-pointer mt-20"
          :class="isDarkMode ? 'bg-dark-900' : 'bg-grey-450'"
          @click="openIconsComponent"
        >
          <Icons
            ref="icons"
            class="cursor-pointer"
            :optional-top="50"
            :optional-coeff="isMobile ? 50 : 0 "
            disable-modal-toggle
            @setIcon="setIcon"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component'

import RadioModule from '@/components/shared/form-controls/RadioModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Icons from '@/components/projects/Icons'
import Themes from '@/components/settings/Themes'

import designConfig from '@/core/design-config'
import { mapState, mapMutations, mapGetters } from 'vuex'
import { settingsService } from '@/services'

export default {
  components: { RadioModule, Themes, ButtonModule, VueSlider, Icons },

  emits: ['back'],

  data () {
    return {
      modes: Object.keys(designConfig.modes),
      columns: Object.keys(designConfig.columns),
      fontSizes: Object.keys(designConfig.fontSizes),
      fontFamilies: Object.keys(designConfig.fontFamilies),
      themes: Object.keys(designConfig.themes),
      marks: Object.keys(designConfig.lineHeights),
      lineHeight: 1.5
    }
  },

  computed: {
    ...mapGetters(['isDarkMode', 'isMobile']),

    ...mapState({
      settings: state => state.settings.settings,
      showBackButton: state => state.settings.showBackButton
    }),

    fontSize () {
      return `font-size: ${designConfig.fontSizes[this.settings.fontSize]}`
    }
  },

  mounted () {
    this.lineHeight = +this.settings.lineHeight
  },

  methods: {
    ...mapMutations(['updateSettings', 'updateDefaultIcon']),

    update (type, value) {
      const design = { ...this.settings }
      design[type] = value

      this.updateSettings({ id: this.settings.id, ...design })
      settingsService.update({ [type]: value })
    },

    splitFontFamily (value) {
      return value.split(' ').join('')
    },

    back () {
      this.$emit('back')
    },

    openIconsComponent () {
      setTimeout(() => {
        this.$refs.icons.toggleModal()
      }, 0)
    },

    setIcon (iconName) {
      this.updateDefaultIcon(iconName)
    }
  }
}
</script>
